/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { getOrgRoles } from 'src/services/apiService';
import { OrgRoles } from 'src/types';
import useAuth from './useAuth';

interface OrgState {
  orgRoles: OrgRoles;
  isLoading: number;
  error: string | null;
  fetchOrgRoles: () => Promise<void>;
}

const defaultOrgContextValue = {
  orgRoles: {
    roles: [],
  },
  isLoading: 0,
  error: null,
  fetchOrgRoles: () => {
    throw new Error('Not implemented');
  },
};

const OrgContext = createContext<OrgState>(defaultOrgContextValue);

const useOrg = (): OrgState => useContext(OrgContext);

export const OrgProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const {
    user: { organisationId, id },
  } = useAuth();

  const [orgRoles, setOrgRoles] = useState<OrgRoles>(
    defaultOrgContextValue.orgRoles
  );
  const [isLoading, setIsLoading] = useState<number>(
    defaultOrgContextValue.isLoading
  );
  const [error, setError] = useState(defaultOrgContextValue.error);

  const fetchOrgRoles = async () => {
    try {
      setIsLoading((prev) => prev + 1);
      setError(null);
      const data = await getOrgRoles(organisationId, id);
      setOrgRoles(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading((prev) => prev - 1);
    }
  };

  useEffect(() => {
    fetchOrgRoles();
    return () => {
      setOrgRoles(defaultOrgContextValue.orgRoles);
      setIsLoading(defaultOrgContextValue.isLoading);
      setError(defaultOrgContextValue.error);
    };
  }, [organisationId]);

  return (
    <OrgContext.Provider
      value={{
        orgRoles,
        isLoading,
        error,
        fetchOrgRoles,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export default useOrg;
